import React, { useState, useEffect } from 'react';
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { 
  Settings, 
  DollarSign, 
  Activity, 
  CheckCircle2, 
  Users, 
  AlertCircle, 
  Clock, 
  Loader2,
  RefreshCw,
  XCircle,
  Package,
  Trash2,
  Save
} from "lucide-react";
import { 
  clearIPRInsights
} from "../../../../ApiCalls/mantisIA";

const AIInsightsSection = ({ 
  data, 
  orderItems = [], 
  isLoadingItems = false, 
  clientId,
  insights = null,
  loading = false,
  error = null,
  onRegenerateInsights,
  onClearInsights
}) => {
  const [regenerating, setRegenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  console.log("orderItems", orderItems);

  // Actualizar lastUpdated cuando cambien los insights
  useEffect(() => {
    if (insights) {
      setLastUpdated(new Date().toISOString());
    }
  }, [insights]);

  // Estas funciones ahora se manejan desde ControlPanelDetail

  // Función para regenerar insights forzadamente
  const handleRegenerateInsights = async () => {
    if (!onRegenerateInsights) return;
    
    setRegenerating(true);
    try {
      await onRegenerateInsights();
    } catch (error) {
      console.error('Error regenerating insights:', error);
    } finally {
      setRegenerating(false);
    }
  };

  // Función para limpiar insights
  const handleClearInsights = async () => {
    if (!onClearInsights || !clientId || !data?.id) return;
    
    setClearing(true);
    try {
      await clearIPRInsights(clientId, data.id);
      console.log('✅ Cleared insights from database');
      onClearInsights();
      setLastUpdated(null);
    } catch (error) {
      console.error('Error clearing insights:', error);
    } finally {
      setClearing(false);
    }
  };

  // Función de prueba con tu ejemplo (mantenida para debugging)
  const testWithProvidedExample = () => {
    const testResponse = `'{\n' +
    '  "summary": {\n' +
    '    "title": "Resumen Ejecutivo",\n' +
    \`    "content": "La Solicitud de Compra (IPR) 196 requiere atención inmediata debido a su estado 'pending' y monto total de $0.00. La falta de especificación en categoría, subcategoría y departamento dificulta el enrutamiento y la aprobación oportuna. Se recomienda completar la información faltante para agilizar el proceso.",\n\` +
    '    "status": "error",\n' +
    '    "recommendations": [\n' +
    '      "Completar los campos faltantes de categoría, subcategoría y departamento en el IPR.",\n' +
    '      "Verificar la justificación del monto total de $0.00 y actualizar si es necesario."\n' +
    '    ]\n' +
    '  },\n' +
    '  "budget_analysis": {\n' +
    '    "title": "Análisis Presupuestal",\n' +
    '    "status": "LOW",\n' +
    '    "budget_usage": 0,\n' +
    '    "remaining_budget": "1200000",\n' +
    '    "insights": [\n' +
    '      "El uso presupuestal actual es 0%, indicando que el IPR aún no impacta el presupuesto.",\n' +
    '      "El impacto financiero es nulo al momento, pero dependerá del monto final que se asigne al IPR.",\n' +
    '      "El principal riesgo presupuestal es la falta de información que impide la asignación presupuestaria correcta."\n' +
    '    ],\n' +
    '    "recommendation": "Asegurar la correcta imputación presupuestaria una vez que el monto y la categoría del IPR sean definidos."\n' +
    '  },\n' +
    '  "cash_flow_analysis": {\n' +
    '    "title": "Análisis de Flujo de Caja",\n' +
    '    "status": "LOW",\n' +
    '    "impact": "Bajo",\n' +
    '    "payment_terms": "Establecer términos de pago estándar (Net 30) una vez aprobado el IPR.",\n' +
    '    "insights": [\n' +
    '      "El impacto en la liquidez es actualmente bajo debido al monto de $0.00, pero cambiará al actualizarse.",\n' +
    '      "El impacto en el flujo de caja dependerá de los términos de pago negociados con el proveedor.",\n' +
    '      "Es crucial definir los términos de pago para prever el impacto en el flujo de caja futuro."\n' +
    '    ],\n' +
    '    "recommendation": "Negociar términos de pago favorables con el proveedor para optimizar el flujo de caja."\n' +
    '  },\n' +
    '  "compliance_analysis": {\n' +
    '    "title": "Cumplimiento de Políticas",\n' +
    '    "status": "MEDIUM",\n' +
    '    "compliance_score": 75,\n' +
    '    "insights": [\n' +
    '      "El cumplimiento es incierto debido a la falta de información sobre la categoría y subcategoría del IPR.",\n' +
    '      "Las políticas aplicables dependerán de la naturaleza de los ítems solicitados.",\n' +
    '      "Existe un riesgo de incumplimiento si la información faltante no se completa correctamente."\n' +
    '    ]\n' +
    '  },\n' +
    '  "approval_flow": {\n' +
    '    "title": "Flujo de Aprobaciones",\n' +
    '    "estimated_time": "1-2 días hábiles",\n' +
    '    "approvers": [\n' +
    '      {"name": "Ana García", "initials": "AG"},\n' +
    '      {"name": "Carlos López", "initials": "CL"}\n' +
    '    ],\n' +
    '    "insights": [\n' +
    '      "El proceso de aprobación se encuentra detenido hasta que se complete la información faltante.",\n' +
    '      "El principal cuello de botella es la falta de información que impide el enrutamiento correcto del IPR.",\n' +
    '      "La eficiencia del flujo se verá afectada si la información no se proporciona de manera oportuna."\n' +
    '    ]\n' +
    '  }\n' +
    '}\n' +
    '\`\`\`'`;

    try {
      // Usar la función de limpieza desde mantisIA.js
      const cleanAIResponse = (response) => {
        let cleaned = response;
        
        // Remover prefijos y sufijos comunes
        cleaned = cleaned.replace(/```json/g, '').replace(/```/g, '');
        
        // Caso especial: Respuesta como string concatenado de JavaScript
        if (cleaned.includes("'\\n' +") || cleaned.includes("` +")) {
          // Convertir las concatenaciones de JavaScript en un string continuo
          cleaned = cleaned.replace(/'\\n'\s*\+\s*/g, '');
          cleaned = cleaned.replace(/`\s*\+\s*/g, '');
          cleaned = cleaned.replace(/\s*\+\s*`/g, '');
          cleaned = cleaned.replace(/'\s*\+\s*/g, '');
          cleaned = cleaned.replace(/\s*\+\s*'/g, '');
          
          // Remover comillas al inicio y final
          cleaned = cleaned.replace(/^[`'"]|[`'"]$/g, '');
        }
        
        // Buscar el contenido JSON válido
        let jsonStart = cleaned.indexOf('{');
        let jsonEnd = cleaned.lastIndexOf('}');
        
        // Si no encuentra llaves, buscar en el contenido limpio
        if (jsonStart === -1) {
          // Remover caracteres no deseados y buscar de nuevo
          cleaned = cleaned.replace(/[\n\r\t]/g, '').replace(/\s+/g, ' ');
          jsonStart = cleaned.indexOf('{');
          jsonEnd = cleaned.lastIndexOf('}');
        }
        
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          cleaned = cleaned.substring(jsonStart, jsonEnd + 1);
        }
        
        // Limpiar caracteres de escape y formato
        cleaned = cleaned.replace(/\\n/g, '');
        cleaned = cleaned.replace(/\\"/g, '"');
        cleaned = cleaned.replace(/\\\\/g, '\\');
        
        // Normalizar espacios
        cleaned = cleaned.replace(/\s+/g, ' ').trim();
        
        return cleaned;
      };

      const cleaned = cleanAIResponse(testResponse);
      console.log('Test - Cleaned response:', cleaned);
      const parsed = JSON.parse(cleaned);
      console.log('Test - Successfully parsed:', parsed);
      // Nota: Esta función de test ya no puede modificar el estado directamente
      // Los insights ahora se manejan desde ControlPanelDetail
      console.log('Test - Parsed insights:', parsed);
    } catch (error) {
      console.error('Test failed:', error);
    }
  };

  // Función para obtener el color del badge según el status
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'HIGH':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'LOW':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded p-3 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
              <Loader2 className="h-3 w-3 text-white animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm">Cargando Insights</h3>
              <p className="text-xs text-purple-600 dark:text-purple-300 truncate">Procesando análisis...</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
              <div className="animate-pulse space-y-2">
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded p-3 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
              <Settings className="h-3 w-3 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm">Insights de IA</h3>
              <p className="text-xs text-purple-600 dark:text-purple-300 truncate">Error en el análisis</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-red-200 dark:border-red-700">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="h-3 w-3 text-red-500" />
            <h4 className="font-semibold text-red-900 dark:text-red-100 text-xs">Error al generar análisis</h4>
          </div>
          <p className="text-xs text-red-700 dark:text-red-300 mb-2 leading-tight">{error}</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded p-3 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
              <Settings className="h-3 w-3 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm">Insights de IA</h3>
              <p className="text-xs text-purple-600 dark:text-purple-300 truncate">No disponible</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
          <div className="text-center py-4">
            <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-2">
              <Settings className="h-4 w-4 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Sin insights disponibles</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* AI Header - Simplificado para sidebar */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded p-3 border border-purple-200 dark:border-purple-800">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
            <Settings className="h-3 w-3 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm">Insights de IA</h3>
            <p className="text-xs text-purple-600 dark:text-purple-300 truncate">Análisis inteligente</p>
          </div>
        </div>
      </div>

      {/* Resumen Ejecutivo */}
      {insights.summary && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center space-x-2 mb-2">
            <Settings className="h-3 w-3 text-blue-500" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.summary.title}</h4>
          </div>
          
          <div className="space-y-2">
            <p className="text-xs text-slate-700 dark:text-slate-300 leading-tight">{insights.summary.content}</p>
            
            {insights.summary.recommendations && insights.summary.recommendations.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2 border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">Recomendaciones:</p>
                <ul className="space-y-1">
                  {insights.summary.recommendations.map((rec, index) => (
                    <li key={index} className="text-xs text-blue-700 dark:text-blue-300 flex items-start space-x-1">
                      <div className="w-1 h-1 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="leading-tight">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Análisis de Stock e Inventario */}
      {insights.stock_analysis && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Package className="h-3 w-3 text-indigo-500" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.stock_analysis.title}</h4>
            </div>
            <Badge className={`text-xs px-1 py-0.5 ${getStatusColor(insights.stock_analysis.status)}`}>
              {insights.stock_analysis.status}
            </Badge>
          </div>
          
          <div className="space-y-2">
            {/* Progress bar de completitud */}
            {insights.stock_analysis.completion_percentage !== undefined && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600 dark:text-slate-400">Items listos</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{insights.stock_analysis.completion_percentage}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5">
                  <div 
                    className="bg-indigo-500 h-1.5 rounded-full" 
                    style={{width: `${insights.stock_analysis.completion_percentage}%`}}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>✅ {insights.stock_analysis.items_ready || 0}</span>
                  <span>⏳ {insights.stock_analysis.items_pending || 0}</span>
                </div>
              </div>
            )}

            {/* Insights de stock */}
            {insights.stock_analysis.insights && (
              <div className="space-y-2 text-xs">
                <div className="font-medium text-slate-700 dark:text-slate-300">Insights de Inventario:</div>
                {insights.stock_analysis.insights.map((insight, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-indigo-500 rounded-full"></div>
                    <span className="text-slate-600 dark:text-slate-400">{insight}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Sugerencias de alternativas */}
            {insights.stock_analysis.alternative_suggestions && insights.stock_analysis.alternative_suggestions.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-3 border border-amber-200 dark:border-amber-700">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">💡 Sugerencias de Alternativas:</p>
                <ul className="space-y-1">
                  {insights.stock_analysis.alternative_suggestions.map((suggestion, index) => (
                    <li key={index} className="text-xs text-amber-700 dark:text-amber-300 flex items-start space-x-2">
                      <div className="w-1 h-1 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recomendación */}
            {insights.stock_analysis.recommendation && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Recomendación:</p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">{insights.stock_analysis.recommendation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Análisis Presupuestal */}
      {insights.budget_analysis && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-3 w-3 text-red-500" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.budget_analysis.title}</h4>
            </div>
            <Badge className={`text-xs px-1 py-0.5 ${getStatusColor(insights.budget_analysis.status)}`}>
              {insights.budget_analysis.status}
            </Badge>
          </div>
          
          <div className="space-y-3">
            {insights.budget_analysis.budget_usage && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600 dark:text-slate-400">Utilización del presupuesto</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{insights.budget_analysis.budget_usage}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{width: `${insights.budget_analysis.budget_usage}%`}}
                  ></div>
                </div>
                {insights.budget_analysis.remaining_budget && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2 border border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-blue-800 dark:text-blue-200 font-medium">
                      Presupuesto restante: $ {insights.budget_analysis.remaining_budget}
                    </p>
                  </div>
                )}
              </div>
            )}

            {insights.budget_analysis.insights && (
              <div className="space-y-2 text-xs">
                {insights.budget_analysis.insights.map((insight, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-600 dark:text-slate-400">{insight}</span>
                  </div>
                ))}
              </div>
            )}

            {insights.budget_analysis.recommendation && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-3 border border-amber-200 dark:border-amber-700">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Recomendación:</p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">{insights.budget_analysis.recommendation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Análisis de Flujo de Caja */}
      {insights.cash_flow_analysis && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Activity className="h-3 w-3 text-orange-500" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.cash_flow_analysis.title}</h4>
            </div>
            <Badge className={`text-xs px-1 py-0.5 ${getStatusColor(insights.cash_flow_analysis.status)}`}>
              {insights.cash_flow_analysis.status}
            </Badge>
          </div>
          
          <div className="space-y-3">
            {insights.cash_flow_analysis.impact && (
              <div className="flex items-start space-x-2">
                <div className="w-3 h-3 bg-blue-100 dark:bg-blue-800 rounded-full mt-1 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Impacto</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{insights.cash_flow_analysis.impact}</p>
                </div>
              </div>
            )}

            {insights.cash_flow_analysis.payment_terms && (
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-2 border border-purple-200 dark:border-purple-700">
                <div className="flex items-center space-x-2 mb-2">
                  <Settings className="h-3 w-3 text-purple-600" />
                  <span className="text-xs font-medium text-purple-800 dark:text-purple-200">Términos de Pago Sugeridos</span>
                </div>
                <p className="text-xs text-purple-700 dark:text-purple-300">{insights.cash_flow_analysis.payment_terms}</p>
              </div>
            )}

            {insights.cash_flow_analysis.insights && (
              <div className="space-y-2 text-xs">
                <div className="font-medium text-slate-700 dark:text-slate-300">Insights de Liquidez:</div>
                {insights.cash_flow_analysis.insights.map((insight, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-600 dark:text-slate-400">{insight}</span>
                  </div>
                ))}
              </div>
            )}

            {insights.cash_flow_analysis.recommendation && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Recomendación:</p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">{insights.cash_flow_analysis.recommendation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cumplimiento de Políticas */}
      {insights.compliance_analysis && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.compliance_analysis.title}</h4>
            </div>
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs px-1 py-0.5">
              {insights.compliance_analysis.compliance_score}%
            </Badge>
          </div>
          
          <div className="space-y-3">
            {insights.compliance_analysis.compliance_score && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600 dark:text-slate-400">Puntuación de cumplimiento</span>
                  <span className="font-medium text-green-600">{insights.compliance_analysis.compliance_score}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{width: `${insights.compliance_analysis.compliance_score}%`}}
                  ></div>
                </div>
              </div>
            )}

            {insights.compliance_analysis.insights && (
              <div className="space-y-2 text-xs">
                <div className="font-medium text-slate-700 dark:text-slate-300">Insights de Políticas:</div>
                {insights.compliance_analysis.insights.map((insight, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    <span className="text-slate-600 dark:text-slate-400">{insight}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Flujo de Aprobaciones */}
      {insights.approval_flow && (
        <div className="bg-white dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Users className="h-3 w-3 text-blue-500" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-xs">{insights.approval_flow.title}</h4>
            </div>
            {insights.approval_flow.estimated_time && (
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3 text-slate-400" />
                <span className="text-xs text-slate-500">{insights.approval_flow.estimated_time}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            {insights.approval_flow.approvers && insights.approval_flow.approvers.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">Aprobadores Requeridos:</p>
                <div className="flex items-center space-x-3">
                  {insights.approval_flow.approvers.map((approver, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-purple-600 dark:text-purple-300">
                          {approver.initials}
                        </span>
                      </div>
                      <span className="text-xs text-blue-700 dark:text-blue-300">{approver.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights.approval_flow.insights && (
              <div className="space-y-2 text-xs">
                <div className="font-medium text-slate-700 dark:text-slate-300">Insights del Proceso:</div>
                {insights.approval_flow.insights.map((insight, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    <span className="text-slate-600 dark:text-slate-400">{insight}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default AIInsightsSection; 